import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import {
  type CreateShippingTermPayload,
  type IShippingTermRepository,
  type UpdateShippingTermPayload,
} from '../../../business-logic/interfaces/IShippingTermRepository'
import { type ShippingTermConstructor } from '../../../entities/ShippingTerm'
import { type ShippingTermApiModel } from '../../api-models/ShippingTermApiModel'
import { mapShippingTermApiModel } from '../../mappers/mapShippingTermApiModel'
import { type GetShippingTermsResponse } from '../../types/GetShippingTermsResponse'

const SHIPPING_TERM_URL = '/api/v3/terms/shipping/'

interface Dependency {
  httpClient: IHttpClient
}

export class ShippingTermRepository implements IShippingTermRepository {
  constructor(private readonly deps: Dependency) {}

  async list(
    params?: RepoFilterParams
  ): Promise<RepoPaginatedResponse<ShippingTermConstructor>> {
    const res = await this.deps.httpClient.get<GetShippingTermsResponse>(
      SHIPPING_TERM_URL,
      {
        params: formatRepoFiltersToApiFilters(params),
      }
    )

    const shippingTerms = res.data.data || []
    const metadata = res.data.metadata

    return {
      data: shippingTerms.map((shippingTerm) =>
        mapShippingTermApiModel(shippingTerm)
      ),
      pagination: {
        count: metadata?.pagination?.count || 0,
        page: metadata?.pagination?.current_page || 0,
        pageSize: metadata?.pagination?.page_size || 0,
        numPages: metadata?.pagination?.num_pages || 0,
      },
    }
  }

  // Omits Angular's stray `id: null` / `text: ""` create fields — the server
  // ignores them
  async create(payload: CreateShippingTermPayload): Promise<{ id: number }> {
    try {
      const res = await this.deps.httpClient.post<{
        data: ShippingTermApiModel
      }>(SHIPPING_TERM_URL, payload)

      return { id: res.data.data.id }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async update(id: number, payload: UpdateShippingTermPayload): Promise<void> {
    try {
      // PUT the whole row (Angular parity)
      await this.deps.httpClient.put(`${SHIPPING_TERM_URL}${id}/`, {
        id,
        ...payload,
      })
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  // Canonical trailing slash — avoids the 301 hop Angular incurs
  async delete(id: number): Promise<void> {
    try {
      await this.deps.httpClient.delete(`${SHIPPING_TERM_URL}${id}/`)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
